import { useCallback, useEffect, useMemo, useState } from "react";
import { MessageSquare, Plus, RefreshCw, Save, Send, Pencil, X } from "lucide-react";
import AdminShell, { Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import DataTable from "@/components/admin/DataTable";
import { api, getErrorMessage } from "@/lib/api";

const emptyForm = {
  purpose: "",
  name: "",
  dlt_template_id: "",
  authkey_sid: "",
  message_body: "",
  category: "Transactional",
  enabled: false,
};

export default function AdminSmsDltSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [config, setConfig] = useState(null);
  const [settings, setSettings] = useState({
    pe_id: "",
    sender: "PAISAC",
    header_id: "",
    entity_name: "",
    company_name: "PaisaCX",
  });
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showAdd, setShowAdd] = useState(false);
  const [testMobile, setTestMobile] = useState("");
  const [testPurpose, setTestPurpose] = useState("MOBILE_VERIFICATION");
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/admin/sms/dlt-config");
      setConfig(data);
      setSettings({
        pe_id: data.pe_id || "",
        sender: data.sender || "PAISAC",
        header_id: data.header_id || "",
        entity_name: data.entity_name || "",
        company_name: data.company_name || "PaisaCX",
      });
      setTemplates(data.templates || []);
      if (!testPurpose && data.templates?.[0]?.purpose) {
        setTestPurpose(data.templates[0].purpose);
      }
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [testPurpose]);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveSettings() {
    setSaving(true);
    setErr("");
    setOk("");
    try {
      const { data } = await api.put("/api/admin/sms/dlt-config", settings);
      setSettings({
        pe_id: data.pe_id || "",
        sender: data.sender || "",
        header_id: data.header_id || "",
        entity_name: data.entity_name || "",
        company_name: data.company_name || "PaisaCX",
      });
      setOk("DLT settings saved.");
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(row) {
    setShowAdd(false);
    setEditing(row.id);
    setForm({
      purpose: row.purpose || "",
      name: row.name || "",
      dlt_template_id: row.dlt_template_id || "",
      authkey_sid: row.authkey_sid || "",
      message_body: row.message_body || "",
      category: row.category || "",
      enabled: Boolean(row.enabled),
    });
  }

  function cancelEdit() {
    setEditing(null);
    setShowAdd(false);
    setForm(emptyForm);
  }

  async function saveTemplate() {
    setSaving(true);
    setErr("");
    setOk("");
    try {
      if (showAdd) {
        await api.post("/api/admin/sms/templates", form);
        setOk("Template added.");
      } else if (editing) {
        await api.patch(`/api/admin/sms/templates/${editing}`, form);
        setOk("Template updated.");
      }
      cancelEdit();
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(row) {
    setErr("");
    try {
      await api.patch(`/api/admin/sms/templates/${row.id}`, { enabled: !row.enabled });
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  async function runTest() {
    if (!testMobile.trim()) {
      setErr("Enter a mobile number for the test SMS.");
      return;
    }
    setTesting(true);
    setErr("");
    setOk("");
    try {
      const { data } = await api.post("/api/admin/sms/test", {
        purpose: testPurpose,
        mobile: testMobile.trim(),
        variables: { otp: "123456" },
      });
      setOk(`Test SMS submitted: ${data.message || "OK"}`);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setTesting(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        key: "purpose",
        header: "Purpose",
        render: (r) => (
          <div>
            <div className="font-semibold text-foreground">{r.purpose}</div>
            <div className="text-[11px] text-muted-foreground">{r.name}</div>
          </div>
        ),
      },
      {
        key: "dlt_template_id",
        header: "DLT Template ID",
        render: (r) => (
          <span className="font-mono text-[12px] text-muted-foreground">
            {r.dlt_template_id || <span className="text-amber-400">Not set</span>}
          </span>
        ),
      },
      {
        key: "category",
        header: "Category",
        render: (r) => <span className="text-muted-foreground">{r.category || "—"}</span>,
      },
      {
        key: "enabled",
        header: "Status",
        render: (r) => (
          <StatusPill
            value={r.enabled ? (r.dlt_template_id ? "Active" : "Enabled") : "Disabled"}
          />
        ),
      },
      {
        key: "actions",
        header: "Actions",
        align: "right",
        render: (r) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => startEdit(r)}
              className="text-[12px] text-primary hover:underline inline-flex items-center gap-1"
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              type="button"
              onClick={() => toggleEnabled(r)}
              className="text-[12px] text-muted-foreground hover:text-white"
            >
              {r.enabled ? "Disable" : "Enable"}
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templates],
  );

  return (
    <AdminShell
      title="SMS / DLT (AuthKey)"
      subtitle="Configure Indian DLT PE ID, sender, and template IDs. SMS goes through PRP or AuthKey (server .env)."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <StatusChip
              label="Active provider"
              ok={Boolean(config?.provider_ready)}
              detail={(config?.sms_provider || "authkey").toUpperCase()}
            />
            <StatusChip
              label="PRP API"
              ok={Boolean(config?.prp_configured)}
              detail={
                config?.prp_configured
                  ? "PRP credentials loaded"
                  : (config?.prp_hint || "Set PRP_API_BASE + PRP_API_KEY in server .env, then restart")
              }
            />
            <StatusChip
              label="AuthKey API"
              ok={Boolean(config?.authkey_configured)}
              detail={config?.authkey_configured ? "AUTHKEY_API_KEY present" : "Optional fallback"}
            />
            <StatusChip
              label="DLT ready"
              ok={Boolean(config?.ready && settings.pe_id && settings.sender)}
              detail={config?.ready ? "PE ID + sender + provider OK" : "Complete PE ID + sender + provider"}
            />
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-line text-muted-foreground text-sm hover:bg-surface-2"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {err ? (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3">{err}</div>
        ) : null}
        {ok ? (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm px-4 py-3">{ok}</div>
        ) : null}

        <Panel className="p-5 space-y-4 max-w-3xl">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-primary" />
            <h3 className="text-[15px] font-semibold text-white">DLT entity & header</h3>
          </div>
          <p className="text-[12px] text-muted-foreground">
            <strong>SMS gateway:</strong> set <code className="text-muted-foreground">SMS_PROVIDER=prp</code> when
            your PE–TM chain is PRP Services (telemarketer). Keep AuthKey as fallback with{" "}
            <code className="text-muted-foreground">SMS_PROVIDER=authkey</code>. DLT PE ID / templates stay in this Admin page.
          </p>
          <p className="text-[12px] text-muted-foreground">
            PaisaCX DLT Principal Entity ID: <strong>1101605610000098046</strong>. Sender name:{" "}
            <strong>PAISAC</strong>. PRP TM ID (for DLT portal chain): <strong>1102461800000042573</strong>.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="DLT Principal Entity ID (PE ID)">
              <input
                value={settings.pe_id}
                onChange={(e) => setSettings({ ...settings, pe_id: e.target.value })}
                className={inputCls}
                placeholder="1101605610000098046"
                autoComplete="off"
              />
            </Field>
            <Field label="Sender / Header">
              <input
                value={settings.sender}
                onChange={(e) => setSettings({ ...settings, sender: e.target.value.toUpperCase() })}
                className={inputCls}
                placeholder="PAISAC"
                maxLength={12}
              />
            </Field>
            <Field label="Header ID (optional reference)">
              <input
                value={settings.header_id}
                onChange={(e) => setSettings({ ...settings, header_id: e.target.value })}
                className={inputCls}
                placeholder="Optional — same as PE ID if your portal uses one ID"
              />
            </Field>
            <Field label="DLT Entity name">
              <input
                value={settings.entity_name}
                onChange={(e) => setSettings({ ...settings, entity_name: e.target.value })}
                className={inputCls}
                placeholder="Entity name as registered on the DLT portal"
              />
            </Field>
            <Field label="Company name (OTP variable)">
              <input
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className={inputCls}
                placeholder="PaisaCX"
              />
            </Field>
          </div>
          <Btn onClick={saveSettings} disabled={saving || loading} data-testid="dlt-save-settings">
            <Save size={14} /> {saving ? "Saving…" : "Save DLT settings"}
          </Btn>
        </Panel>

        <Panel>
          <div className="p-4 border-b border-line flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-[14px] font-semibold text-white">Template mappings</h3>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Two ways to send OTP: (A) <strong>AuthKey SID</strong> — template text lives in AuthKey portal,
                no message body here; or (B) <strong>DLT ID + message body</strong> — paste exact approved text.
              </p>
            </div>
            <Btn
              onClick={() => {
                setEditing(null);
                setShowAdd(true);
                setForm(emptyForm);
              }}
            >
              <Plus size={14} /> Add template
            </Btn>
          </div>

          {(showAdd || editing) && (
            <div className="p-4 border-b border-line bg-background space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-white">{showAdd ? "Add template" : "Edit template"}</h4>
                <button type="button" onClick={cancelEdit} className="text-muted-foreground hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Purpose key">
                  <input
                    value={form.purpose}
                    onChange={(e) => setForm({ ...form, purpose: e.target.value.toUpperCase().replace(/\s+/g, "_") })}
                    className={inputCls}
                    placeholder="MOBILE_VERIFICATION"
                    disabled={!showAdd}
                  />
                </Field>
                <Field label="Template name">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    placeholder="Mobile Verification"
                  />
                </Field>
                <Field label="DLT Template ID (117717… long ID)">
                  <input
                    value={form.dlt_template_id}
                    onChange={(e) => setForm({ ...form, dlt_template_id: e.target.value })}
                    className={inputCls}
                    placeholder="1177178782905528070"
                  />
                </Field>
                <Field label="AuthKey SID (short — optional, skips message body)">
                  <input
                    value={form.authkey_sid}
                    onChange={(e) => setForm({ ...form, authkey_sid: e.target.value })}
                    className={inputCls}
                    placeholder="e.g. 40528 from AuthKey portal"
                  />
                </Field>
                <Field label="Category">
                  <input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className={inputCls}
                    placeholder="Transactional"
                  />
                </Field>
              </div>
              <Field label="Approved message body (only if not using AuthKey SID)">
                <textarea
                  value={form.message_body}
                  onChange={(e) => setForm({ ...form, message_body: e.target.value })}
                  className={`${inputCls} min-h-[90px] font-mono text-[12px]`}
                  placeholder="Skip if AuthKey SID is set. Otherwise paste exact DLT text with {#var#} or {#otp#}."
                />
              </Field>
              <label className="inline-flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                Enabled
              </label>
              <div>
                <Btn onClick={saveTemplate} disabled={saving}>
                  <Save size={14} /> {saving ? "Saving…" : "Save template"}
                </Btn>
              </div>
            </div>
          )}

          <DataTable columns={columns} rows={templates} empty={loading ? "Loading…" : "No templates yet"} testid="sms-dlt-templates" />
        </Panel>

        <Panel className="p-5 space-y-3 max-w-xl">
          <h3 className="text-[15px] font-semibold text-white">Test SMS</h3>
          <p className="text-[12px] text-muted-foreground">
            Sends a real AuthKey request using the selected purpose. OTP purposes fill {"{otp}"} with 123456.
            Ensure the template is enabled with a DLT ID + message body first.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Purpose">
              <select
                value={testPurpose}
                onChange={(e) => setTestPurpose(e.target.value)}
                className={inputCls}
              >
                {templates.map((t) => (
                  <option key={t.purpose} value={t.purpose}>
                    {t.purpose}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Mobile (10-digit or +91)">
              <input
                value={testMobile}
                onChange={(e) => setTestMobile(e.target.value)}
                className={inputCls}
                placeholder="9876543210"
              />
            </Field>
          </div>
          <Btn onClick={runTest} disabled={testing || loading}>
            <Send size={14} /> {testing ? "Sending…" : "Send test SMS"}
          </Btn>
        </Panel>
      </div>
    </AdminShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-[12px] text-muted-foreground">
      <span className="mb-1.5 block font-medium">{label}</span>
      {children}
    </label>
  );
}

function StatusChip({ label, ok, detail }) {
  return (
    <div className="rounded-xl border border-line bg-background px-3 py-2.5 min-w-[140px]">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`text-sm font-semibold mt-1 ${ok ? "text-emerald-400" : "text-amber-400"}`}>
        {ok ? "Configured" : "Not set"}
      </div>
      {detail ? <div className="text-[11px] text-muted-foreground mt-0.5">{detail}</div> : null}
    </div>
  );
}

const inputCls =
  "w-full h-10 px-3 rounded-lg bg-background border border-line text-foreground text-sm outline-none focus:border-primary";
